import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  PawPrint,
  GraduationCap,
  Stethoscope,
  UtensilsCrossed,
  Sparkles,
  Leaf,
  LifeBuoy,
  BadgeCheck,
  ChevronRight,
} from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import { getInitiative, initiatives } from "@/data/sample";
import { useEffect } from "react";

const ICONS: Record<string, typeof PawPrint> = {
  PawPrint,
  GraduationCap,
  Stethoscope,
  UtensilsCrossed,
  Sparkles,
  Leaf,
  LifeBuoy,
};

export const Route = createFileRoute("/initiatives/$slug")({
  loader: ({ params }) => {
    const initiative = getInitiative(params.slug);
    if (!initiative) throw notFound();
    return { initiative };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return { meta: [{ title: "Initiative not found" }, { name: "robots", content: "noindex" }] };
    const i = loaderData.initiative;
    return {
      meta: [
        { title: `${i.name} — Manavta` },
        { name: "description", content: i.short },
        { property: "og:title", content: `${i.name} — Manavta` },
        { property: "og:description", content: i.short },
      ],
    };
  },
  component: Page,
  notFoundComponent: NotFound,
});

function NotFound() {
  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold text-[var(--maroon-deep)]">
          Initiative not found
        </h1>
        <p className="mt-2 text-foreground/70">This initiative may have moved or been renamed.</p>
        <Link
          to="/initiatives"
          className="mt-6 inline-flex rounded-full bg-[var(--maroon)] px-5 py-2 text-sm font-semibold text-[var(--cream)]"
        >
          See all initiatives
        </Link>
      </section>
    </SiteShell>
  );
}

function Page() {
  const { initiative } = Route.useLoaderData();
  const navigate = useNavigate();
  const Icon = ICONS[initiative.icon] ?? PawPrint;

  // Animal Seva has its own full platform page — redirect there
  useEffect(() => {
    if (initiative.slug === "animal-seva") {
      navigate({ to: "/initiatives/animal-seva", replace: true });
    }
  }, [initiative.slug, navigate]);

  const others = initiatives.filter((i) => i.slug !== initiative.slug).slice(0, 3);

  return (
    <SiteShell>
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: `linear-gradient(180deg, ${initiative.color}12, transparent 60%)` }}
        />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="text-xs text-foreground/60">
            <Link to="/">Home</Link> <ChevronRight className="inline h-3 w-3" />{" "}
            <Link to="/initiatives">Initiatives</Link> <ChevronRight className="inline h-3 w-3" />{" "}
            <span className="text-foreground/85">{initiative.name}</span>
          </div>
          <div className="mt-6 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <div
                  className="grid h-14 w-14 place-items-center rounded-2xl text-white shadow-[var(--shadow-soft)]"
                  style={{ background: initiative.color }}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <p
                    className="text-xs font-semibold uppercase tracking-[0.18em]"
                    style={{ color: initiative.color }}
                  >
                    {initiative.tagline}
                  </p>
                  <h1 className="font-display text-4xl font-semibold text-[var(--maroon-deep)] sm:text-5xl">
                    {initiative.name}
                  </h1>
                </div>
              </div>
              <p className="mt-6 text-lg text-foreground/75">{initiative.description}</p>
              {!initiative.live && (
                <div className="mt-5 rounded-2xl border border-[var(--saffron)]/40 bg-[var(--cream-2)] p-4 text-sm text-[var(--maroon-deep)]">
                  <strong>Programme page under construction.</strong> Donations are already open
                  through our unified donation flow — choose <em>{initiative.name}</em> as the
                  initiative on the donation page.
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/donate"
                  search={{ initiative: initiative.slug } as never}
                  className="rounded-full px-6 py-3 text-sm font-semibold text-white shadow-[var(--shadow-soft)] transition hover:opacity-90"
                  style={{ background: initiative.color }}
                >
                  Donate to {initiative.name}
                </Link>
                <Link
                  to="/volunteer"
                  className="rounded-full border border-border bg-white px-6 py-3 text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)]"
                >
                  Volunteer
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1 rounded-full px-3 py-3 text-sm font-medium text-foreground/70 hover:text-[var(--maroon)]"
                >
                  Partner with us <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
                <img src={initiative.cover} alt="" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {initiative.stats.map((s: { label: string; value: string }) => (
                  <div
                    key={s.label}
                    className="rounded-2xl border border-border/60 bg-card p-4 text-center shadow-[var(--shadow-soft)]"
                  >
                    <p className="font-display text-xl font-semibold text-[var(--maroon-deep)]">
                      {s.value}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-wide text-foreground/60">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
          Programmes
        </p>
        <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)]">
          What your donation funds
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {initiative.programs.map((p: { title: string; body: string }) => (
            <div
              key={p.title}
              className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-soft)]"
            >
              <div
                className="grid h-10 w-10 place-items-center rounded-xl text-white"
                style={{ background: initiative.color }}
              >
                <BadgeCheck className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{p.title}</h3>
              <p className="mt-1.5 text-sm text-foreground/70">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[var(--cream-2)] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
                Explore more
              </p>
              <h2 className="mt-2 font-display text-2xl font-semibold text-[var(--maroon-deep)]">
                Other initiatives
              </h2>
            </div>
            <Link to="/initiatives" className="text-sm font-medium text-[var(--maroon)]">
              View all →
            </Link>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {others.map((i) => {
              const OtherIcon = ICONS[i.icon] ?? PawPrint;
              return (
                <Link
                  key={i.slug}
                  to="/initiatives/$slug"
                  params={{ slug: i.slug }}
                  className="group rounded-2xl border border-border/60 bg-white p-5 shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="grid h-10 w-10 place-items-center rounded-xl text-white"
                      style={{ background: i.color }}
                    >
                      <OtherIcon className="h-5 w-5" />
                    </div>
                    <p className="font-display text-lg font-semibold">{i.name}</p>
                  </div>
                  <p className="mt-3 text-sm text-foreground/70 line-clamp-2">{i.short}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--maroon)]">
                    Learn more <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
